import { useEffect, useState } from 'react';
import { SwalCustomClass } from '../../components/general/toastSwals/swalCustomClass';
import { useNavigate } from 'react-router-dom';
import { b64toBlob } from '../../../shared/utils/files';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { ExportMessages } from '../../../shared/messages/exportMessages';
import {
    asyncGetSurveys,
    asyncPostExportData,
} from '../../../services/survey.service';
import dayjs from 'dayjs';
import { ISelect } from '../../../shared/types/shared';
import { SurveyInterface } from '../../../shared/types/survey.interface';

export const useExport = () => {
    const navigate = useNavigate();
    const { MESSAGE_OK, MESSAGE_ERROR } = ExportMessages;
    const [loading, setLoading] = useState(false);
    const [surveyList, setSurveyList] = useState<SurveyInterface[]>([]);
    const [surveyCodeOptions, setSurveyCodeOptions] = useState<ISelect[]>([]);
    const [surveyTitleOptions, setSurveyTitleOptions] = useState<ISelect[]>([]);

    useEffect(() => {
        asyncGetSurveys().then((body) => {
            if (body.items !== undefined) {
                const surveys = body.items.items || [];

                setSurveyList(surveys);

                const codeOpts = surveys.map((s: SurveyInterface) => ({
                    id: s.code,
                    name: s.code,
                }));

                const titleOpts = surveys.map((s: SurveyInterface) => ({
                    id: s.title,
                    name: s.title,
                }));

                setSurveyCodeOptions(codeOpts);
                setSurveyTitleOptions(titleOpts);
            }
        });
    }, []);

    const formik = useFormik({
        initialValues: {
            startDate: '',
            endDate: '',
            surveyCode: '',
            surveyTitle: '',
        },
        validationSchema: Yup.object({
            startDate: Yup.date().required('La fecha inicial es requerida'),
            endDate: Yup.date()
                .required('La fecha final es requerida')
                .test(
                    'fechas-validas',
                    'Debe ingresar fecha final si hay inicial, y esta debe ser mayor.',
                    function (value) {
                        const { startDate } = this.parent;
                        if (startDate && !value) return false;
                        if (
                            startDate &&
                            value &&
                            dayjs(value).isBefore(dayjs(startDate))
                        )
                            return false;
                        return true;
                    }
                ),
            surveyCode: Yup.string().required(
                'El código de la encuesta es requerida'
            ),
            surveyTitle: Yup.string().required(
                'El título de la encuesta es requerida'
            ),
        }),
        onSubmit: async (values) => {
            const selectedSurvey = surveyList.find(
                (item) =>
                    item.code === values.surveyCode &&
                    item.title === values.surveyTitle
            );
            exportData(selectedSurvey?.id || '');
        },
    });

    const exportData = async (surveyId: string) => {
        setLoading(true);

        try {
            const body: any = await asyncPostExportData(
                surveyId,
                dayjs(formik.values.startDate).toISOString(),
                dayjs(formik.values.endDate).toISOString()
            );

            if (body.items !== undefined) {
                const blob = b64toBlob(
                    body.items,
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                );
                const blobUrl = URL.createObjectURL(blob);
                window.location = blobUrl as any;

                await SwalCustomClass.fire({
                    text: MESSAGE_OK,
                    icon: 'success',
                    iconColor: '#1d1d1d',
                });
            } else {
                await SwalCustomClass.fire({
                    text: MESSAGE_ERROR,
                    icon: 'error',
                    iconColor: '#1d1d1d',
                });
            }
        } catch (error) {
            await SwalCustomClass.fire({
                text: 'Ocurrió un error inesperado',
                icon: 'error',
                iconColor: '#1d1d1d',
            });
        } finally {
            setLoading(false);
        }
    };

    return {
        formik,
        navigate,
        loading,
        surveyCodeOptions,
        surveyTitleOptions,
        surveyList,
    };
};
